import type { ViewClient } from '../../api/viewClientApiService';
import { CLIENT_LABELS } from '../../constants/labels';

/**
 * Returns the trimmed string value or an em-dash placeholder for null/empty values.
 */
function fmt(value: string | null | undefined): string {
  return value?.trim() || '—';
}

/**
 * Formats a 5-digit zip code string into Polish postal format (XX-XXX).
 * Handles both raw digits (e.g. "83383") and already-formatted values (e.g. "83-383").
 */
function fmtZipCode(value: string | null | undefined): string {
  const digits = value?.trim().replace(/\D/g, '') ?? '';
  if (digits.length === 5) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }
  return value?.trim() || '—';
}

/** Props for a single labelled read-only field. */
interface InfoFieldProps {
  label: string;
  value: string;
  testId?: string;
}

/**
 * Renders a stacked label/value pair used across all billing detail sections.
 */
const InfoField = ({ label, value, testId }: InfoFieldProps) => (
  <div className="flex min-w-0 flex-col gap-2">
    <h2 className="text-bodySmall font-normal text-on-surface-variant">
      {label}
    </h2>
    <p
      className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10"
      data-testid={testId}
    >
      {value}
    </p>
  </div>
);

/** Props for the ViewClientBillingDetails component. */
interface ViewClientBillingDetailsProps {
  client: ViewClient | null;
}

/**
 * Displays the "Dane do rozliczeń" tab of the View Client page.
 *
 * Renders two cards:
 * - **Dane firmy** — company name, NIP, REGON, KRS
 * - **Adres do faktury** — invoice address (street, line 2, zip, city, province)
 *
 * All fields fall back to an em-dash when the value is absent.
 */
const ViewClientBillingDetails = ({
  client,
}: ViewClientBillingDetailsProps) => {
  const billing = client?.billingDetails ?? null;

  return (
    <div className="flex flex-col gap-6 m-1.5">
      {/* Company info card */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-lg p-4">
        <p className="text-titleDefault font-medium text-on-surface-variant">
          {CLIENT_LABELS.SECTION_BILLING_COMPANY_INFO}
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={CLIENT_LABELS.FIELD_BILLING_COMPANY_NAME}
            value={fmt(billing?.companyName)}
            testId="view-client-billing-company-name"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_BILLING_NIP}
            value={fmt(billing?.nip)}
            testId="view-client-billing-nip"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_BILLING_REGON}
            value={fmt(billing?.regon)}
            testId="view-client-billing-regon"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_BILLING_KRS}
            value={fmt(billing?.krs)}
            testId="view-client-billing-krs"
          />
        </div>
      </div>

      {/* Invoice address card */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p className="text-titleDefault font-medium text-on-surface-variant">
          {CLIENT_LABELS.SECTION_BILLING_INVOICE_ADDRESS}
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={CLIENT_LABELS.FIELD_INVOICE_ADDRESS_LINE_1}
            value={fmt(billing?.invoiceAddress?.addressLine1)}
            testId="view-client-invoice-address-line-1"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_INVOICE_ADDRESS_LINE_2}
            value={fmt(billing?.invoiceAddress?.addressLine2)}
            testId="view-client-invoice-address-line-2"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_INVOICE_ZIP_CODE}
            value={fmtZipCode(billing?.invoiceAddress?.zipCode)}
            testId="view-client-invoice-zip-code"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_INVOICE_CITY}
            value={fmt(billing?.invoiceAddress?.city)}
            testId="view-client-invoice-city"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_INVOICE_PROVINCE}
            value={fmt(billing?.invoiceAddress?.province)}
            testId="view-client-invoice-province"
          />
        </div>
      </div>
    </div>
  );
};

export default ViewClientBillingDetails;
